<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content>
        <div class="sm:tw-flex tw-items-center tw-justify-between" v-if="isLoading">
          <div class="tw-flex tw-items-center">
            <base-skeleton-loader class="tw-h-16 tw-w-16 md:tw-h-24 md:tw-w-24 tw-rounded-full tw-flex-shrink-0" />
            <div class="tw-ml-4">
              <base-skeleton-loader class="tw-h-3 sm:tw-h-4 tw-w-36 sm:tw-w-48 tw-mt-2.5" />
              <base-skeleton-loader class="tw-h-2 sm:tw-h-2.5 tw-w-16 tw-mt-1 sm:tw-mt-2.5" />
              <div class="tw-flex tw-items-center tw-space-x-4 tw-mt-1.5 md:tw-mt-2">
                <base-skeleton-loader v-for="i in 2" :key="i" class="tw-rounded-full tw-h-8 tw-w-8 tw-mt-px sm:tw-mt-0 md:tw-mt-px" />
              </div>
            </div>
          </div>
          <div class="tw-mt-4 sm:tw-mt-0 tw-mb-0.5 sm:tw-mb-0">
            <base-skeleton-loader class="tw-h-8 tw-w-full sm:tw-w-32" />
          </div>
        </div>
        <div class="sm:tw-flex tw-items-center tw-justify-between" v-else>
          <div class="tw-flex tw-items-center">
            <div class="tw-h-16 tw-w-16 md:tw-h-24 md:tw-w-24 tw-flex-shrink-0">
              <Avatar :business-id="plan.businessId" avatar-type="business" />
            </div>
            <div class="tw-ml-4">
              <h1 class="tw-text-xl sm:tw-text-2xl md:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">{{ plan.planName }}</h1>
              <div class="tw-text-gray-500 tw-text-xs sm:tw-text-sm tw--mt-1">
                {{ plan.businessName }}
              </div>
              <div class="tw-flex tw-items-center tw-space-x-4 tw-mt-1">
                <base-button v-if="businessContacts.length > 0" type="secondary" class="tw-rounded-full tw-pr-2 tw-pl-2" @click="handlePhoneClick">
                  <phone-icon class="tw-h-3.5 tw-w-3.5" />
                </base-button>
                <base-button type="secondary" class="tw-rounded-full tw-pr-2 tw-pl-2" @click="email">
                  <mail-icon class="tw-h-3.5 tw-w-3.5" />
                </base-button>
              </div>
            </div>
          </div>
          <div class="tw-mt-4 sm:tw-mt-0">
            <base-button v-if="plan.useCreditsSetting === true" class="tw-w-full sm:tw-w-auto" size="sm" @click="$refs.rechargeCreditsModal.show">Recharge a Credit</base-button>
          </div>
        </div>
      </base-content>
    </div>
    <base-content>
      <base-card
        class="tw-grid tw-grid-cols-1 tw-overflow-hidden tw-divide-y tw-divide-gray-200 md:tw-divide-y-0 md:tw-divide-x tw-p-0"
        :class="[plan.useCreditsSetting === true ? 'md:tw-grid-cols-3' : 'md:tw-grid-cols-2']"
      >
        <div class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6">
          <dt class="tw-text-sm tw-font-light tw-text-gray-600 tw-text-center">
            Next Invoice
          </dt>
          <dd class="tw-flex tw-justify-center tw-items-baseline">
            <base-skeleton-loader class="tw-mt-3 tw-mb-2 sm:tw-mb-5 tw-h-3 tw-w-24" v-if="isLoading" />
            <div class="tw-flex tw-items-baseline tw-text-2xl tw-font-thin tw-text-primary" v-else>
              {{ formatDate(plan.nextInvoiceDate) }}
            </div>
          </dd>
        </div>
        <div v-if="plan.useCreditsSetting === true" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6">
          <dt class="tw-text-sm tw-font-light tw-text-gray-600 tw-text-center">
            Credit Balance
          </dt>
          <dd class="tw-flex tw-justify-center tw-items-baseline">
            <base-skeleton-loader class="tw-mt-4 sm:tw-mt-3 tw-mb-4 sm:tw-mb-5 tw-h-3 tw-w-24" v-if="isLoading" />
            <div class="tw-flex tw-items-baseline" v-else>
              <div class="tw-flex tw-flex-col tw-justify-center tw-items-center" v-for="item in plan.itemCategories" :key="item.businessItemCategoryId">
                <span class="tw-text-2xl tw-font-thin tw-text-primary">{{ item.creditBalance }}</span
                ><span class="tw-text-xs tw-text-gray-500 tw-leading-none tw-text-center">{{ item.name }}</span>
              </div>
            </div>
          </dd>
        </div>
        <div class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6">
          <dt class="tw-text-sm tw-font-light tw-text-gray-600 tw-text-center">
            Price
          </dt>
          <dd class="tw-flex tw-justify-center tw-items-baseline">
            <base-skeleton-loader class="tw-mt-3 tw-mb-2 sm:tw-mb-5 tw-h-3 tw-w-24" v-if="isLoading" />

            <div class="tw-flex tw-items-baseline" v-else>
              <Money-Format class="tw-text-2xl tw-font-thin tw-text-primary" :value="plan.priceCents" :locale="locale" :currency-code="plan.currency" subunits-value />
            </div>
          </dd>
        </div>
      </base-card>
      <base-tabs class="tw-mt-2 tw-mb-2 sm:tw-mb-4" @click="tabClick($event)">
        <base-tab v-for="tab in tabs" :key="tab.title" :title="tab.title" :selected="currentComponent === tab.component" />
      </base-tabs>
      <keep-alive>
        <component :is="currentComponent" :user-id="userId" :user-business-plan-id="userBusinessPlanId" :use-credits-setting="plan.useCreditsSetting" />
      </keep-alive>
    </base-content>
    <recharge-credits-modal
      ref="rechargeCreditsModal"
      :business-id="plan.businessId"
      :user-id="userId"
      :user-business-plan-id="plan.userBusinessPlanId"
      :credit-unit-price-in-cents="plan.creditUnitPriceInCents"
      :credit-unit-tax-in-cents="plan.creditUnitTaxInCents"
      :currency="plan.currency"
      :plan-name="plan.planName"
      @member_update="getPlan()"
    />
  </div>
</template>

<script>
import Avatar from "@/components/Avatar";
import MoneyFormat from "vue-money-format";
import { PhoneIcon, MailIcon, MapIcon } from "@/assets/svgs";
import moment from "moment";
import RechargeCreditsModal from "@/views/user/components/RechargeCreditsModal";
import { mapGetters } from "vuex";

export default {
  components: {
    Avatar,
    MoneyFormat,
    PhoneIcon,
    MailIcon,
    MapIcon,
    UserPlanDetails: () => import("@/views/user/UserPlanDetails"),
    UserPlanCharges: () => import("@/views/user/UserPlanCharges"),
    UserPlanCredits: () => import("@/views/user/UserPlanCredits"),
    UserPlanTrackingList: () => import("@/views/user/UserPlanTrackingList"),
    UserPlanShippedHistoryList: () => import("@/views/user/UserPlanShippedHistoryList"),

    RechargeCreditsModal,
  },
  props: { userId: String, userBusinessPlanId: String, businessId: String },
  data() {
    return {
      isPlanLoading: false,
      areLocationsLoading: false,
      businessContacts: [],
      plan: {},
      locations: [],
      tabs: [
        { title: "Details", component: "UserPlanDetails", route: "details" },
        { title: "Charges", component: "UserPlanCharges", route: "charges" },
        { title: "Credits", component: "UserPlanCredits", route: "credits" },
        { title: "Order Tracking", component: "UserPlanTrackingList", route: "tracking" },
        { title: "Shipment History", component: "UserPlanShippedHistoryList", route: "history" },
      ],
    };
  },
  computed: {
    ...mapGetters("cognito", { userName: "name", userEmail: "email" }),
    currentComponent() {
      return this.$route.meta.component;
    },
    locale() {
      return window.navigator.language;
    },
    isLoading() {
      return this.isPlanLoading || this.areLocationsLoading;
    },
    homeLocation() {
      return this.locations.find((l) => l.businessLocationId === this.plan.homeLocationId) || {};
    },
    stats() {
      return [{ title: "Next Invoice", value: this.formatDate(this.plan.nextInvoiceDate) }, { title: "Credit Balance", value: "test" }, { title: "Price", value: "test" }];
    },
  },
  created() {
    this.getPlan();
    this.getLocations();
    this.getBusinessContacts();
  },
  methods: {
    formatDate(date) {
      if (!date) return "-";
      return new moment.utc(date).local().format("MMM Do, YYYY");
    },
    async getPlan() {
      try {
        this.isPlanLoading = true;
        const response = await this.$axios.get(`/users/${this.userId}/plans/${this.userBusinessPlanId}`);
        this.plan = response.data;
      } finally {
        this.isPlanLoading = false;
      }
    },
    async getLocations() {
      try {
        this.areLocationsLoading = true;
        const response = await this.$axios.get(`/users/${this.userId}/businesses/${this.businessId}/locations`);
        this.locations = response.data;
      } finally {
        this.areLocationsLoading = false;
      }
    },
    tabClick(tabTitle) {
      const tab = this.tabs.find((t) => t.title === tabTitle);
      this.$router.push(`/users/${this.userId}/businesses/${this.businessId}/plans/${this.userBusinessPlanId}/${tab.route}`);
    },
    email() {
      if (this.businessContacts.length >= 1) {
        window.open(`mailto:${this.businessContacts[0].email}`, "_self");
      } else {
        window.open(`mailto:${this.plan.businessEmail}`, "_self");
      }
    },
    handlePhoneClick() {
      if (this.locations.length >= 1) {
        window.open(`tel:${this.businessContacts[0].phone}`, "self");
      } else {
        window.open(`tel:number`, "self");
      }
    },
    async getBusinessContacts() {
      await this.$axios.get(`/businesses/${this.businessId}/contacts`).then((response) => {
        this.businessContacts = response.data.sort((a, b) => {
          if (a.contactType < b.contactType) {
            return 1;
          }
          if (a.contactType > b.contactType) {
            return -1;
          }
          return 0;
        });
      });
    },
  },
};
</script>
